import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { adminContractsAPI } from '../../services/api';
import RateCardEditor from '../../components/admin/RateCardEditor';

/**
 * Admin → Contracts → Settings: how a signed document becomes a billing
 * agreement in ConnectWise. Types and billing cycles are ConnectWise's own,
 * read live; nothing here is typed as an id.
 */
export default function ContractSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['contract-settings'], queryFn: () => adminContractsAPI.settings().then((r) => r.data) });
  const [form, setForm] = useState(null);
  const [state, setState] = useState({ busy: false, error: '', saved: false });
  const [cardType, setCardType] = useState(null);

  useEffect(() => {
    if (data?.settings) setForm({ ...data.settings, reminders: data.reminders || { enabled: true, afterDays: [3, 7] } });
  }, [data]);

  if (isLoading || !form) return <div className="p-6 flex items-center gap-2 text-neutral-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  const set = (patch) => { setForm({ ...form, ...patch }); setState({ ...state, saved: false }); };
  const save = async () => {
    setState({ busy: true, error: '', saved: false });
    try {
      await adminContractsAPI.saveSettings(form);
      queryClient.invalidateQueries({ queryKey: ['contract-settings'] });
      setState({ busy: false, error: '', saved: true });
    } catch (e) {
      setState({ busy: false, error: e.response?.data?.error || 'Could not save.', saved: false });
    }
  };
  const label = 'block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1';
  const help = 'text-xs text-neutral-500 dark:text-neutral-400 mt-1';
  const typeSelect = (value, onChange) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)} className="input w-full">
      <option value="">Choose…</option>
      {data.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );
  const cycleSelect = (value, onChange, empty) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)} className="input w-full">
      <option value="">{empty}</option>
      {data.cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <Link to="/admin/contracts" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"><ArrowLeft className="w-4 h-4" /> Contracts</Link>
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Contract settings</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">How a signed document becomes a billing agreement in ConnectWise.</p>
      </div>

      {data.check?.ok === false && (
        <div className="flex gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-200">
          <AlertCircle className="w-4 h-4 mt-0.5" /> ConnectWise could not be read: {data.check.error}. The portal&apos;s ConnectWise member needs Finance › Agreements (add, edit, inquire).
        </div>
      )}

      <div className="rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800 p-6 space-y-4">
        <h2 className="text-lg font-semibold dark:text-white">Signing reminders</h2>
        <label className="flex items-center gap-2 text-sm dark:text-neutral-200">
          <input type="checkbox" checked={form.reminders.enabled} onChange={(e) => set({ reminders: { ...form.reminders, enabled: e.target.checked } })} />
          Email a reminder to anyone who has not signed yet
        </label>
        <div>
          <span className={label}>Days after their link was sent</span>
          <input
            value={form.reminders.afterDays.join(', ')}
            onChange={(e) => set({ reminders: { ...form.reminders, afterDays: e.target.value.split(/[\s,]+/).filter(Boolean).map(Number) } })}
            disabled={!form.reminders.enabled}
            className="input w-48"
            placeholder="3, 7"
          />
          <p className={help}>One reminder for each number, then none. The reminder carries a fresh link; the old one stops working. Nobody waiting for their turn, or for the contract before theirs, is reminded.</p>
        </div>
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800 p-6 space-y-5">
        <h2 className="text-lg font-semibold dark:text-white">Quotes, orders and SOWs</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div><span className={label}>Agreement type</span>{typeSelect(form.defaultAgreementTypeId, (v) => set({ defaultAgreementTypeId: v }))}
            <p className={help}>A signed order adds its lines to the client&apos;s agreement of this type, or creates one.</p></div>
          <div><span className={label}>Billing cycle</span>{cycleSelect(form.defaultBillingCycleId, (v) => set({ defaultBillingCycleId: v }), "The type's default")}</div>
          <div><span className={label}>Lines start</span>
            <select value={form.startRule} onChange={(e) => set({ startRule: e.target.value })} className="input w-full">
              <option value="first_of_next_month">The first of next month</option>
              <option value="signing_date">The day it was signed</option>
            </select>
            <p className={help}>The first of the month avoids a part-month first invoice.</p></div>
          <label className="flex items-start gap-2 text-sm dark:text-neutral-200 self-center">
            <input type="checkbox" checked={form.quotesAutoCreate} onChange={(e) => set({ quotesAutoCreate: e.target.checked })} className="mt-1" />
            <span>Create the agreement as soon as a quote is signed. Quotes that need a decision (a line with no product, several agreements to choose between) still wait for review. Uploaded contracts always do.</span>
          </label>
        </div>
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800 p-6 space-y-4">
        <h2 className="text-lg font-semibold dark:text-white">Coverage and rates</h2>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          What a new agreement of each type covers, and the rates it charges for particular kinds of work instead of their standard
          rate (Onsite (Emergency) at 175 with a three-hour minimum, say). A new agreement starts with its type&apos;s card and can be
          changed for that client before it is created. Lines added to an existing agreement leave its coverage and rates alone.
        </p>
        <div className="max-w-sm"><span className={label}>Agreement type</span>
          {typeSelect(cardType ?? form.defaultAgreementTypeId, (v) => setCardType(v))}</div>
        {(cardType ?? form.defaultAgreementTypeId) && (
          <RateCardEditor
            value={(form.rateCards || {})[String(cardType ?? form.defaultAgreementTypeId)]}
            onChange={(card) => set({ rateCards: { ...(form.rateCards || {}), [String(cardType ?? form.defaultAgreementTypeId)]: card } })}
          />
        )}
        <p className={help}>Saved with the settings below. Copy from an agreement you have already set up to start a type&apos;s card from it.</p>
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800 p-6 space-y-5">
        <h2 className="text-lg font-semibold dark:text-white">Master Services Agreements</h2>
        <div className="space-y-2 text-sm dark:text-neutral-200">
          <label className="flex items-start gap-2">
            <input type="radio" checked={form.msaMode === 'parent'} onChange={() => set({ msaMode: 'parent' })} className="mt-1" />
            <span><strong>A parent agreement.</strong> A signed MSA creates a &quot;Master Services Agreement&quot; agreement that bills nothing, holds the signed PDF and its dates, and the client&apos;s billing agreements are created under it. Renewals and everything an MSA governs are visible in ConnectWise.</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="radio" checked={form.msaMode === 'company'} onChange={() => set({ msaMode: 'company' })} className="mt-1" />
            <span><strong>Filed on the company.</strong> The signed MSA is attached to the client company; billing agreements stand alone.</span>
          </label>
        </div>
        {form.msaMode === 'parent' && (
          <div className="grid gap-4 md:grid-cols-2">
            <div><span className={label}>Agreement type for MSAs</span>{typeSelect(form.msaAgreementTypeId, (v) => set({ msaAgreementTypeId: v }))}
              <p className={help}>Create a type such as &quot;Master Services Agreement&quot; in ConnectWise first if you have none.</p></div>
            <div><span className={label}>Billing cycle for MSAs</span>{cycleSelect(form.msaBillingCycleId, (v) => set({ msaBillingCycleId: v }), 'Same as the billing agreements')}
              <p className={help}>ConnectWise only puts an agreement under a parent with the same billing cycle. The parent bills nothing itself.</p></div>
          </div>
        )}
      </div>

      {state.error && <div className="flex gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-200"><AlertCircle className="w-4 h-4 mt-0.5" /> {state.error}</div>}
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={state.busy} className="btn-primary inline-flex items-center gap-2">{state.busy && <Loader2 className="w-4 h-4 animate-spin" />} Save settings</button>
        {state.saved && <span className="inline-flex items-center gap-1 text-sm text-green-700"><CheckCircle className="w-4 h-4" /> Saved</span>}
      </div>
    </div>
  );
}
